import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Share2,
  Map,
  Plus,
  DollarSign,
  Calendar,
  Users,
  Settings,
  Loader2,
  X,
  CalendarDays,
} from 'lucide-react';
import ChangeDatesModal from '../components/ChangeDatesModal';
import BudgetSlider from '../components/BudgetSlider';
import { supabase } from '../lib/supabase';
import ItineraryDay from '../components/ItineraryDay';
import ItineraryMap from '../components/ItineraryMap';
import AddActivityModal from '../components/AddActivityModal';
import EditItemModal from '../components/EditItemModal';
import { generateItineraryPDF } from '../lib/pdfGenerator';
import type {
  Itinerary,
  Trip,
  ItineraryItem,
  ItineraryDay as ItineraryDayType,
} from '../types/itinerary';
import { calculateItineraryTotal } from '../lib/scheduler';
import toast from 'react-hot-toast';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';

interface ItineraryPageProps {
  user: any;
}

export default function ItineraryPage({ user }: ItineraryPageProps) {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [activeDay, setActiveDay] = useState<number>(1);
  const [showCostBreakdown, setShowCostBreakdown] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDatesModal, setShowDatesModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ItineraryItem | null>(null);
  const [selectedDayForAdd, setSelectedDayForAdd] = useState<ItineraryDayType | null>(null);
  const [budgetMin, setBudgetMin] = useState(0);
  const [budgetMax, setBudgetMax] = useState(trip?.budget || 5000);

  useEffect(() => {
    if (tripId) {
      loadTripAndItinerary();
    }
  }, [tripId]);

  const loadTripAndItinerary = async () => {
    setLoading(true);
    try {
      // Load trip
      const { data: tripData } = await supabase
        .from('trips')
        .select('*')
        .eq('id', tripId)
        .single();

      setTrip(tripData as Trip);

      // Load itinerary days
      const { data: daysData } = await supabase
        .from('itinerary_days')
        .select('*')
        .eq('trip_id', tripId)
        .order('day_number', { ascending: true });

      // Load itinerary items
      const dayIds = daysData?.map((d) => d.id) || [];
      let items: ItineraryItem[] = [];

      if (dayIds.length > 0) {
        const { data: itemsData } = await supabase
          .from('itinerary_items')
          .select('*')
          .in('day_id', dayIds)
          .order('start_time', { ascending: true });
        items = (itemsData as ItineraryItem[]) || [];
      }

      // Merge
      const days: ItineraryDayType[] = (daysData || []).map((day) => ({
        ...day,
        items: items.filter((item) => item.day_id === day.id),
        totalCost: items
          .filter((item) => item.day_id === day.id)
          .reduce((sum, item) => sum + item.cost, 0),
      })) as ItineraryDayType[];

      const totalCost = calculateItineraryTotal({ days } as Itinerary);

      setItinerary({
        id: `itinerary-${tripId}`,
        tripId: tripId!,
        title: tripData?.title || 'Itinerary',
        days,
        totalCost,
        currency: tripData?.currency || 'USD',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Set active day to first day
      if (days.length > 0) {
        setActiveDay(days[0].dayNumber);
      }
    } catch (err) {
      console.error('Error loading itinerary:', err);
      toast.error('Failed to load itinerary');
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = (dayId: string) => {
    const day = itinerary?.days.find((d) => d.id === dayId);
    if (day) {
      setSelectedDayForAdd(day);
      setShowAddModal(true);
    }
  };

  const handleEditItem = (item: ItineraryItem) => {
    setEditingItem(item);
    setShowEditModal(true);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Delete this item from your itinerary?')) return;

    try {
      await supabase.from('itinerary_items').delete().eq('id', itemId);
      toast.success('Item deleted');
      loadTripAndItinerary();
    } catch (err) {
      console.error('Error deleting item:', err);
      toast.error('Failed to delete item');
    }
  };

  const handleItemAdded = (newItem: ItineraryItem) => {
    // Refresh the itinerary
    loadTripAndItinerary();
  };

  const handleItemUpdated = (updatedItem: ItineraryItem) => {
    // Refresh the itinerary
    loadTripAndItinerary();
  };

  const handleExportPDF = async () => {
    if (!trip || !itinerary) {
      toast.error('No itinerary to export');
      return;
    }

    try {
      await generateItineraryPDF(trip, itinerary, {
        includeBudget: true,
        orientation: 'portrait',
      });
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${trip?.title} - Voyage AI`,
          text: `Check out my trip itinerary to ${trip?.destination}!`,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900">Trip not found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/trip/${tripId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{trip.title}</h1>
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <span className="flex items-center gap-1">
                    <Map className="h-3 w-3" />
                    {trip.destination}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {new Date(trip.startDate).toLocaleDateString()} -{' '}
                    {new Date(trip.endDate).toLocaleDateString()}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDatesModal(true)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Change Dates"
              >
                <CalendarDays className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={() => setShowCostBreakdown(!showCostBreakdown)}
                className={`p-2 rounded-lg transition ${
                  showCostBreakdown
                    ? 'bg-primary-100 text-primary-600'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
                title="Cost Breakdown"
              >
                <DollarSign className="h-5 w-5" />
              </button>
              <button
                onClick={handleShare}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Share"
              >
                <Share2 className="h-5 w-5 text-gray-600" />
              </button>
              <button
                onClick={handleExportPDF}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                title="Export PDF"
              >
                <Download className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Day Tabs */}
          {itinerary && itinerary.days.length > 0 && (
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
              {itinerary.days.map((day) => (
                <button
                  key={day.id}
                  onClick={() => setActiveDay(day.dayNumber)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeDay === day.dayNumber
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Day {day.dayNumber}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Cost Breakdown Sidebar */}
      {showCostBreakdown && itinerary && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setShowCostBreakdown(false)}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative w-full max-w-sm bg-white shadow-xl h-full overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold">Cost Breakdown</h2>
              <button
                onClick={() => setShowCostBreakdown(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {['flight', 'hotel', 'activity', 'transport', 'meal'].map(
                (type) => {
                  const typeItems = itinerary.days
                    .flatMap((d) => d.items)
                    .filter((i) => i.type === type);
                  const typeTotal = typeItems.reduce(
                    (sum, i) => sum + i.cost,
                    0
                  );
                  if (typeTotal === 0) return null;

                  return (
                    <div
                      key={type}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            type === 'flight'
                              ? 'bg-blue-100 text-blue-600'
                              : type === 'hotel'
                              ? 'bg-purple-100 text-purple-600'
                              : type === 'activity'
                              ? 'bg-green-100 text-green-600'
                              : type === 'transport'
                              ? 'bg-orange-100 text-orange-600'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {type === 'flight' && '✈️'}
                          {type === 'hotel' && '🏨'}
                          {type === 'activity' && '📍'}
                          {type === 'transport' && '🚗'}
                          {type === 'meal' && '🍽️'}
                        </div>
                        <div>
                          <p className="font-medium capitalize">{type}s</p>
                          <p className="text-sm text-gray-500">
                            {typeItems.length} items
                          </p>
                        </div>
                      </div>
                      <p className="font-semibold">
                        {itinerary.currency} {(typeTotal || 0).toLocaleString()}
                      </p>
                    </div>
                  );
                }
              )}

              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <p className="text-lg font-bold">Total</p>
                  <p className="text-xl font-bold text-primary-600">
                    {itinerary.currency} {(itinerary.totalCost || 0).toLocaleString()}
                  </p>
                </div>
                {trip.budget && (
                  <p
                    className={`text-sm mt-2 ${
                      (itinerary.totalCost || 0) > trip.budget
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}
                  >
                    {(itinerary.totalCost || 0) > trip.budget
                      ? `${itinerary.currency} ${((itinerary.totalCost || 0) - trip.budget).toLocaleString()} over budget`
                      : `${itinerary.currency} ${(trip.budget - (itinerary.totalCost || 0)).toLocaleString()} remaining`}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interactive Map Section */}
      {itinerary && itinerary.days.length > 0 && (
        <div className="max-w-4xl mx-auto px-4 py-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Trip Map</h2>
          <ErrorBoundary
            fallback={
              <div className="bg-gray-100 rounded-xl p-8 text-center">
                <p className="text-gray-500">Map temporarily unavailable</p>
              </div>
            }
          >
            <ItineraryMap days={itinerary.days} height="350px" />
          </ErrorBoundary>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <ErrorBoundary
          fallback={
            <div className="bg-red-50 rounded-xl p-8 text-center">
              <p className="text-red-600 mb-4">Something went wrong loading the itinerary</p>
              <button
                onClick={() => loadTripAndItinerary()}
                className="btn-primary"
              >
                Retry
              </button>
            </div>
          }
        >
          {itinerary && itinerary.days.length > 0 ? (
            <div className="space-y-6">
              {itinerary.days.map((day) => (
                <div
                  key={day.id}
                  className={
                    activeDay === day.dayNumber || activeDay === 0
                      ? 'block'
                      : 'hidden'
                  }
                >
                  <ItineraryDay
                    day={day}
                    onAddItem={handleAddActivity}
                    onEditItem={handleEditItem}
                    onDeleteItem={handleDeleteItem}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                No itinerary yet
              </h2>
              <p className="text-gray-600 mb-6">
                Start planning your trip by adding activities
              </p>
              <button
                onClick={() => navigate(`/trip/${tripId}/chat`)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Start Planning
              </button>
            </div>
          )}
        </ErrorBoundary>
      </main>

      {/* Floating Action Button */}
      {itinerary && itinerary.days.length > 0 && (
        <button
          onClick={() => {
            if (itinerary.days.length > 0) {
              setSelectedDayForAdd(itinerary.days[0]);
              setShowAddModal(true);
            }
          }}
          className="fixed bottom-6 right-6 bg-primary-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-primary-700 transition-colors flex items-center gap-2 font-medium"
        >
          <Plus className="h-5 w-5" />
          Add Activity
        </button>
      )}

      {/* Add Activity Modal */}
      {selectedDayForAdd && (
        <AddActivityModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          day={selectedDayForAdd}
          existingItems={selectedDayForAdd.items}
          onItemAdded={handleItemAdded}
          tripId={tripId!}
        />
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <EditItemModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          item={editingItem}
          onItemUpdated={handleItemUpdated}
        />
      )}

      {/* Change Dates Modal */}
      {trip && (
        <ChangeDatesModal
          isOpen={showDatesModal}
          onClose={() => setShowDatesModal(false)}
          trip={trip}
          onDatesChanged={loadTripAndItinerary}
        />
      )}

      {/* Budget Slider */}
      {itinerary && (
        <div className="fixed bottom-6 left-6 z-30">
          <BudgetSlider
            minBudget={budgetMin}
            maxBudget={budgetMax}
            currentTotal={itinerary.totalCost}
            currency={itinerary.currency}
            className="w-72"
          />
        </div>
      )}
    </div>
  );
}
